import { useCallback, useEffect, useState } from "react";
import { useAgentBridge } from "../../app/AgentBridge";
import { fetchCustomDoc, saveCustomDoc } from "./api";
import { Step1Input } from "./Step1Input";
import { Step2Edit } from "./Step2Edit";
import { StepIndicator } from "./StepIndicator";
import {
  buildCustomSlug,
  clearPersistedCustomState,
  isValidDocName,
  loadPersistedCustomState,
  persistCustomState,
  type CustomState,
} from "./state";

export function CustomWizard() {
  const { subscribe } = useAgentBridge();
  const [state, setState] = useState<CustomState>(loadPersistedCustomState);
  const [busy, setBusy] = useState(false);
  const [remoteContent, setRemoteContent] = useState<string>("");
  const [hint, setHint] = useState<string | null>(null);

  useEffect(() => {
    persistCustomState(state);
  }, [state]);

  const setStep = useCallback(
    (step: CustomState["step"]) =>
      setState((prev) => {
        const maxReachedStep = (step > prev.maxReachedStep ? step : prev.maxReachedStep) as CustomState["maxReachedStep"];
        return { ...prev, step, maxReachedStep };
      }),
    [],
  );

  const startDocument = useCallback(async () => {
    const name = state.docName.trim();
    if (!isValidDocName(name)) {
      setHint("Bitte einen gültigen Dokumentnamen eingeben.");
      return;
    }
    setBusy(true);
    setHint(null);
    try {
      const slug = buildCustomSlug(name);
      await saveCustomDoc(slug, state.initialContent);
      setState((prev) => ({
        ...prev,
        currentSlug: slug,
        step: 2,
        maxReachedStep: 2,
      }));
      setRemoteContent(state.initialContent);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setHint("Fehler: " + message);
    } finally {
      setBusy(false);
    }
  }, [state.docName, state.initialContent]);

  const newDocument = useCallback(() => {
    clearPersistedCustomState();
    setState(loadPersistedCustomState());
    setRemoteContent("");
    setHint(null);
    setBusy(false);
  }, []);

  // Live-refresh when the agent rewrites the doc on disk.
  useEffect(() => {
    return subscribe((msg) => {
      if (msg.type === "file_changed" && msg.fileType === "custom_document") {
        const slug = state.currentSlug;
        if (!slug) return;
        void (async () => {
          const content = await fetchCustomDoc(slug);
          if (content == null) return;
          setRemoteContent(content);
        })();
      }
    });
  }, [subscribe, state.currentSlug]);

  // Re-fetch when navigating back to Step 2 after a reload.
  useEffect(() => {
    if (state.step !== 2) return;
    if (!state.currentSlug) return;
    if (remoteContent) return;
    void (async () => {
      const content = await fetchCustomDoc(state.currentSlug!);
      if (content != null) setRemoteContent(content);
    })();
  }, [state.step, state.currentSlug, remoteContent]);

  return (
    <div className="custom-wizard">
      <StepIndicator
        step={state.step}
        maxReachedStep={state.maxReachedStep}
        onStepClick={setStep}
      />

      {hint && (
        <div className="info-box" role="status">
          {hint}{" "}
          <button type="button" className="link-button" onClick={() => setHint(null)}>
            Schließen
          </button>
        </div>
      )}

      {state.step === 1 && (
        <Step1Input
          docName={state.docName}
          initialContent={state.initialContent}
          busy={busy}
          onChange={(patch) => setState((prev) => ({ ...prev, ...patch }))}
          onStart={() => void startDocument()}
        />
      )}
      {state.step === 2 && (
        <Step2Edit
          slug={state.currentSlug}
          remoteContent={remoteContent}
          exportTemplate={state.exportTemplate}
          onTemplateChange={(id) => setState((prev) => ({ ...prev, exportTemplate: id }))}
          onNew={newDocument}
        />
      )}
    </div>
  );
}
